import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Bell, BellOff, Smartphone, CheckCircle, AlertCircle } from 'lucide-react';
import {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  registerServiceWorker,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  isSubscribedToPushNotifications,
  showTestNotification,
  isIOS,
  isPWA,
  getNotificationError
} from '../utils/notifications';

const NotificationSettings = () => {
  const { API } = useContext(AuthContext);
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // Notification preferences
  const [preferences, setPreferences] = useState({
    notify_devotional: true,
    notify_journal: true,
    notify_family_activities: true,
    notify_achievements: true,
    notify_public_prayers: true,
    notify_personal_prayers: true,
    notify_daily_reflection: true
  });
  const [loadingPreferences, setLoadingPreferences] = useState(false);

  const fetchPreferences = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications/subscription`);
      if (response.data) {
        setPreferences({
          notify_devotional: response.data.notify_devotional ?? true,
          notify_journal: response.data.notify_journal ?? true,
          notify_family_activities: response.data.notify_family_activities ?? true,
          notify_achievements: response.data.notify_achievements ?? true,
          notify_public_prayers: response.data.notify_public_prayers ?? true,
          notify_personal_prayers: response.data.notify_personal_prayers ?? true,
          notify_daily_reflection: response.data.notify_daily_reflection ?? true
        });
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  }, [API]);

  const checkNotificationStatus = useCallback(async () => {
    setLoading(true);
    try {
      const supported = isNotificationSupported();
      setIsSupported(supported);

      if (supported) {
        const perm = getNotificationPermission();
        setPermission(perm);

        if (perm === 'granted') {
          await registerServiceWorker();
          const subscribed = await isSubscribedToPushNotifications();
          setIsSubscribed(subscribed);
        }
      }
    } catch (error) {
      console.error('Error checking notification status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkNotificationStatus();
  }, [checkNotificationStatus]);

  useEffect(() => {
    if (isSubscribed) {
      fetchPreferences();
    }
  }, [isSubscribed, fetchPreferences]);

  const updatePreference = async (key, value) => {
    setLoadingPreferences(true);
    try {
      await axios.put(`${API}/notifications/preferences`, { [key]: value });
      setPreferences(prev => ({ ...prev, [key]: value }));
      toast.success(`${value ? 'Enabled' : 'Disabled'} ${key.replace('notify_', '').replace('_', ' ')} notifications`);
    } catch (error) {
      toast.error('Failed to update preference');
      // Revert the change
      setPreferences(prev => ({ ...prev, [key]: !value }));
    } finally {
      setLoadingPreferences(false);
    }
  };

  const handleEnableNotifications = async () => {
    setUpdating(true);
    try {
      const granted = await requestNotificationPermission();
      
      if (granted) {
        await registerServiceWorker();
        const token = localStorage.getItem('token');
        await subscribeToPushNotifications(API, token);
        setPermission('granted');
        setIsSubscribed(true);
        toast.success('Push notifications enabled! Check your test notification.');
        await showTestNotification();
      } else {
        if (isIOS()) {
          toast.error('Permission denied. Go to Settings > Safari and allow notifications for this site.', {
            duration: 6000
          });
        } else {
          toast.error('Permission denied. Please enable notifications in your browser settings.');
        }
      }
    } catch (error) {
      console.error('Failed to enable notifications:', error);
      
      // Provide more specific error messages
      if (error.message?.includes('not supported')) {
        toast.error('Notifications not supported. Please install the app to your home screen first.');
      } else if (error.name === 'NotAllowedError') {
        toast.error('Notification permission was denied. Check your browser settings.');
      } else if (error.name === 'NotSupportedError') {
        toast.error('Push notifications are not supported on this device.');
      } else {
        toast.error(`Failed to enable notifications: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleDisableNotifications = async () => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      await unsubscribeFromPushNotifications(API, token);
      setIsSubscribed(false);
      toast.success('Push notifications disabled');
    } catch (error) {
      console.error('Failed to disable notifications:', error);
      toast.error('Failed to disable notifications');
    } finally {
      setUpdating(false);
    }
  };

  const handleTestNotification = async () => {
    try {
      await showTestNotification();
      toast.success('Test notification sent!');
    } catch (error) {
      toast.error('Failed to send test notification');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
        <Navigation />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-lg text-slate-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50" data-testid="notification-settings">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="mb-8 animate-fade-in">
          <h1 className="text-4xl font-heading font-bold text-[#2B6B7F] mb-2">
            Notification Settings
          </h1>
          <p className="text-slate-600">Manage your push notifications and reminders</p>
        </div>

        {!isSupported ? (
          <>
            <Card className="border-yellow-200 bg-yellow-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-yellow-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-yellow-900 mb-2">
                      {isIOS() && !isPWA() 
                        ? 'Installation Required for iPhone' 
                        : 'Notifications Not Supported'}
                    </h3>
                    <p className="text-sm text-yellow-800 mb-3">
                      {getNotificationError()?.message}
                    </p>
                    {getNotificationError()?.instructions && (
                      <div className="bg-white/50 rounded-lg p-3 mt-3">
                        <p className="text-sm font-semibold text-yellow-900 mb-2">How to enable notifications:</p>
                        <ol className="list-decimal list-inside space-y-1 text-sm text-yellow-800">
                          {getNotificationError().instructions.map((instruction) => (
                            <li key={instruction}>{instruction}</li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Show install instructions more prominently for iOS users */}
            {isIOS() && !isPWA() && (
              <Card className="mt-6 bg-gradient-to-br from-blue-50 to-teal-50 border-blue-200">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <Smartphone className="w-6 h-6 text-[#2B6B7F]" />
                    <CardTitle className="text-xl">📱 Install to Home Screen First</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-3">Step-by-Step Guide:</h4>
                    <div className="space-y-3">
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#2B6B7F] text-white flex items-center justify-center flex-shrink-0 font-bold">
                          1
                        </div>
                        <p className="text-sm text-slate-700 pt-1">
                          Tap the <strong>Share button</strong> at the bottom of Safari (square with upward arrow)
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#2B6B7F] text-white flex items-center justify-center flex-shrink-0 font-bold">
                          2
                        </div>
                        <p className="text-sm text-slate-700 pt-1">
                          Scroll down the menu and tap <strong>"Add to Home Screen"</strong>
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#2B6B7F] text-white flex items-center justify-center flex-shrink-0 font-bold">
                          3
                        </div>
                        <p className="text-sm text-slate-700 pt-1">
                          Tap <strong>"Add"</strong> in the top right corner
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#2B6B7F] text-white flex items-center justify-center flex-shrink-0 font-bold">
                          4
                        </div>
                        <p className="text-sm text-slate-700 pt-1">
                          <strong>Close Safari</strong> and open the app from your home screen
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center flex-shrink-0 font-bold">
                          ✓
                        </div>
                        <p className="text-sm text-slate-700 pt-1">
                          Come back to this page and enable notifications!
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-blue-100 border border-blue-300 rounded-lg p-3 mt-4">
                    <p className="text-xs text-blue-900">
                      <strong>Note:</strong> Notifications on iPhone only work when the app is installed to your home screen. This is a requirement from Apple for all web apps.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <>
            {/* Status Card */}
            <Card className="mb-6">
              <CardHeader>
                <div className="flex items-center gap-3">
                  {isSubscribed ? (
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                      <BellOff className="w-6 h-6 text-slate-400" />
                    </div>
                  )}
                  <div>
                    <CardTitle className="text-xl">
                      Push Notifications {isSubscribed ? 'Enabled' : 'Disabled'}
                    </CardTitle>
                    <CardDescription>
                      {isSubscribed 
                        ? 'You will receive daily reminders and updates'
                        : 'Enable notifications to stay on track with your spiritual journey'
                      }
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={isSubscribed}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          handleEnableNotifications();
                        } else {
                          handleDisableNotifications();
                        }
                      }}
                      disabled={updating}
                      data-testid="notification-toggle"
                    />
                    <span className="text-sm font-medium text-slate-700">
                      {isSubscribed ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  {isSubscribed && (
                    <Button
                      onClick={handleTestNotification}
                      variant="outline"
                      size="sm"
                      className="border-[#2B6B7F] text-[#2B6B7F]"
                      data-testid="test-notification-btn"
                    >
                      Send Test Notification
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Notification Types */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-xl">Notification Preferences</CardTitle>
                <CardDescription>
                  Customize which reminders you'd like to receive
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Daily Devotional */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#7BA888]/10 flex items-center justify-center flex-shrink-0">
                      <Bell className="w-5 h-5 text-[#7BA888]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-1">📖 Daily Devotional</h4>
                      <p className="text-sm text-slate-600">
                        Morning reminder for Bible reading (7 AM)
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.notify_devotional}
                    onCheckedChange={(checked) => updatePreference('notify_devotional', checked)}
                    disabled={!isSubscribed || loadingPreferences}
                  />
                </div>

                {/* Daily Journal */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#2B6B7F]/10 flex items-center justify-center flex-shrink-0">
                      <Bell className="w-5 h-5 text-[#2B6B7F]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-1">📝 Daily Journal</h4>
                      <p className="text-sm text-slate-600">
                        Evening reminder for journaling (8 PM)
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.notify_journal}
                    onCheckedChange={(checked) => updatePreference('notify_journal', checked)}
                    disabled={!isSubscribed || loadingPreferences}
                  />
                </div>

                {/* Family Activities */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                      <Bell className="w-5 h-5 text-pink-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-1">👨‍👩‍👧 Family Activities</h4>
                      <p className="text-sm text-slate-600">
                        Weekend reminder for family faith time (Saturdays)
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.notify_family_activities}
                    onCheckedChange={(checked) => updatePreference('notify_family_activities', checked)}
                    disabled={!isSubscribed || loadingPreferences}
                  />
                </div>

                {/* Achievements */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#D4A574]/10 flex items-center justify-center flex-shrink-0">
                      <Bell className="w-5 h-5 text-[#D4A574]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-1">🏆 Achievements</h4>
                      <p className="text-sm text-slate-600">
                        Celebrate milestones in your discipleship journey
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.notify_achievements}
                    onCheckedChange={(checked) => updatePreference('notify_achievements', checked)}
                    disabled={!isSubscribed || loadingPreferences}
                  />
                </div>

                {/* Public Prayer Wall posts */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50" data-testid="pref-public-prayers-row">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Bell className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-1">🙏 Public Prayer Wall</h4>
                      <p className="text-sm text-slate-600">
                        Notify me whenever anyone shares a new prayer on the public wall
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.notify_public_prayers}
                    onCheckedChange={(checked) => updatePreference('notify_public_prayers', checked)}
                    disabled={!isSubscribed || loadingPreferences}
                    data-testid="pref-public-prayers-toggle"
                  />
                </div>

                {/* Personal prayer updates */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50" data-testid="pref-personal-prayers-row">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                      <Bell className="w-5 h-5 text-rose-500" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-1">💗 My Personal Prayer Updates</h4>
                      <p className="text-sm text-slate-600">
                        Group prayers, approvals, edit decisions, and when someone prays for you
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.notify_personal_prayers}
                    onCheckedChange={(checked) => updatePreference('notify_personal_prayers', checked)}
                    disabled={!isSubscribed || loadingPreferences}
                    data-testid="pref-personal-prayers-toggle"
                  />
                </div>

                {/* Daily Reflection */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50" data-testid="pref-daily-reflection-row">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <Bell className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-1">🕊️ Daily Reflection</h4>
                      <p className="text-sm text-slate-600">
                        A short daily quote inspired by Brother Lawrence's <em>Practice of the Presence of God</em>
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.notify_daily_reflection}
                    onCheckedChange={(checked) => updatePreference('notify_daily_reflection', checked)}
                    disabled={!isSubscribed || loadingPreferences}
                    data-testid="pref-daily-reflection-toggle"
                  />
                </div>

                {!isSubscribed && (
                  <p className="text-sm text-amber-600 text-center pt-2">
                    Enable notifications above to customize your preferences
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Install Instructions */}
            <Card className="bg-gradient-to-br from-blue-50 to-teal-50 border-blue-200">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Smartphone className="w-6 h-6 text-[#2B6B7F]" />
                  <CardTitle className="text-xl">Install on Your Phone</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <h4 className="font-semibold text-slate-800 mb-2">📱 iPhone (Safari)</h4>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-slate-600">
                    <li>Tap the Share button (square with arrow)</li>
                    <li>Scroll down and tap "Add to Home Screen"</li>
                    <li>Tap "Add" in the top right</li>
                  </ol>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800 mb-2">🤖 Android (Chrome)</h4>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-slate-600">
                    <li>Tap the menu button (three dots)</li>
                    <li>Tap "Add to Home Screen"</li>
                    <li>Tap "Add"</li>
                  </ol>
                </div>
                <p className="text-sm text-slate-600 italic mt-3">
                  Once installed, the app will work like a native app with notifications!
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationSettings;
